import { withNamespaces } from "react-i18next";
import { Card, CardHeader, CardBody, Button, Row, Col, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { RiCloseCircleLine, RiInformationLine } from "react-icons/ri";
import { useContext, useState } from "react";
import QueryContext from "../../../../contexts/QueryContext";
import { useQuery } from "@tanstack/react-query";
import LicenseManagementService from "src/modules/3rd-party-management/apis/admin/LicenseManagementService";
import { toast } from "react-toastify";

const CreditConfirmationModal = ({
    isOpen,
    toggle,
    onConfirm,
    finalizeIsInProcess,
    t
}) => {
    return (
        <Modal isOpen={isOpen} toggle={toggle} centered>

            <ModalHeader toggle={toggle}>
                {t('Confirm Submission')}
            </ModalHeader>

            <ModalBody>
                <p className="mb-2">
                    {t('Please confirm the credit usage')}
                </p>
            </ModalBody>

            <ModalFooter>
                <Button 
                    color="secondary" 
                    outline 
                    onClick={toggle}
                    disabled={finalizeIsInProcess}
                >
                    {t('Cancel')}
                </Button>

                <Button 
                    color="primary" 
                    onClick={onConfirm}
                    disabled={finalizeIsInProcess}
                >
                    {finalizeIsInProcess && (
                        <Spinner color="white" className="me-1" size="sm" />
                    )}

                    {t('Confirm and Submit')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const Confirmation = ({
    t,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const [ showCreditUsageConfirmModal, setShowCreditUsageConfirmModal ] = useState(false);

    const {
        queryType,
        queryData,
        handleFinalizeQueryMutation
    } = useContext(QueryContext);

    const fetchLicenseDetails = useQuery({
        queryKey: [
            '3rd-party-management-fetch-compliance-license-details',
            queryType,
            queryData?.id
        ],
        queryFn: async () => {
            const service = LicenseManagementService.getInstance();

            return await service.fetchComplianceCredits({
                queryId: queryData?.id,
                queryType: queryType
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('Failed to load {{creditLabel}} details. Please try again.', {
                creditLabel: 'credit'
            }), {
                type: 'error'
            });
        }
    });

    if (fetchLicenseDetails.isError) {
        return (
            <Row>
                <Col sm="12">
                    <Alert color="danger" className="d-flex align-items-center justify-content-start gap-2">
                        <RiCloseCircleLine size={40} />
                        <div>
                            <strong>
                                {t('Error')}
                            </strong>
                            <p className="mb-0">
                                {t('Unable to load {{creditLabel}} details. Please refresh the page or try again later. If the issue persists, contact your administrator.', {
                                    creditLabel: 'credit'
                                })}
                            </p>
                        </div>
                    </Alert>
                </Col>
            </Row>
        )
    }

    const {
        activeItemsCount,
        monitoringCredit,
        screeningCredit
    } = fetchLicenseDetails.data || {};

    const currentAvailableCredit = parseInt(
        queryType === 'screening' ? 
        screeningCredit?.currentAvailable : 
        monitoringCredit?.currentAvailable
    ) || 0;

    const canClickNext = currentAvailableCredit - activeItemsCount >= 0;

    return (
        <Row>
            <Col sm="12">
                {fetchLicenseDetails.isFetching ? (
                    <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '160px' }} />
                ) : (
                    <Alert color="info" className="d-flex align-items-center justify-content-start gap-2">
                        <RiInformationLine size={40} />
                        <div>
                            <strong>{t('{{creditsLabel}} Required', {
                                creditsLabel: 'Credits'
                            })}</strong>
                            <p className="mb-0">
                                {t('Review your available {{creditsLabel}} and confirm the cost before submitting the query.', {
                                    creditsLabel: 'credits'
                                })}
                            </p>
                        </div>
                    </Alert>
                )}
            </Col>

            <Col sm="12">
                <Card
                    className={`border`}
                >
                    <CardHeader className={'bg-white'}>
                        <h5
                            className={`mb-1 text-dark`}
                        >
                            {t('{{creditLabel}} Usage', {
                                creditLabel: 'Credit'
                            })}
                        </h5>
                    </CardHeader>

                    <CardBody>
                        <Row>
                            <Col sm="12" md="4" lg="3" className="mb-3">
                                <strong>
                                    {t('Your balance')}:
                                </strong>
                            </Col>
                            <Col sm="12" md="8" lg="9" className="mb-3">
                                {fetchLicenseDetails.isFetching ? (
                                    <div
                                        className="dt-field dt-skeleton"
                                        style={{ width: '70%', height: '15px' }}
                                    />
                                ) : (
                                    currentAvailableCredit > 0 ? currentAvailableCredit : (
                                        <span className="text-danger">
                                            <RiCloseCircleLine className="me-1" size={24} />
                                            {t('Not Enough {{creditLabel}}', {
                                                creditLabel: 'Credit'
                                            })}
                                        </span>
                                    )
                                )}
                            </Col>

                            <Col sm="12" md="4" lg="3" className="mb-3">
                                <strong>
                                    {t('Total search items')}:
                                </strong>
                            </Col>
                            <Col sm="12" md="8" lg="9" className="mb-3">
                                {fetchLicenseDetails.isFetching ? (
                                    <div
                                        className="dt-field dt-skeleton"
                                        style={{ width: '70%', height: '15px' }}
                                    />
                                ) : activeItemsCount}
                            </Col>

                            <Col sm="12" md="4" lg="3" className="mb-3">
                                <strong>
                                    {t('Total credit(s)')}:
                                </strong>
                            </Col>
                            <Col sm="12" md="8" lg="9" className="mb-3">
                                {fetchLicenseDetails.isFetching ? (
                                    <div
                                        className="dt-field dt-skeleton"
                                        style={{ width: '70%', height: '15px' }}
                                    />
                                ) : (
                                    `${activeItemsCount} Credits`
                                )}
                            </Col>

                            <Col sm="12" md="4" lg="3" className="mb-3">
                                <strong>
                                    {t('Balance after submission')}:
                                </strong>
                            </Col>
                            <Col sm="12" md="8" lg="9" className="mb-4">
                                {fetchLicenseDetails.isFetching ? (
                                    <div
                                        className="dt-field dt-skeleton"
                                        style={{ width: '70%', height: '15px' }}
                                    />
                                ) : (
                                    `${currentAvailableCredit - activeItemsCount} Credits`
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col sm="12">
                                {/* Submit Button */}
                                <div className="d-flex justify-content-between">
                                    <Button
                                        color="secondary"
                                        type="button"
                                        outline
                                        onClick={onCancelButtonClicked}
                                    >
                                        {t('Cancel')}
                                    </Button>

                                    <div>
                                        <Button
                                            color="secondary"
                                            onClick={onBackButtonClicked}
                                            className="me-2"
                                            type="button"
                                        >
                                            {t('Back')}
                                        </Button>

                                        <Button
                                            color="primary"
                                            type="button"
                                            onClick={() => setShowCreditUsageConfirmModal(true)}
                                            disabled={!canClickNext}
                                        >
                                            {t('Submit Query')}
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <CreditConfirmationModal 
                    t={t}
                    finalizeIsInProcess={handleFinalizeQueryMutation.isLoading}
                    onConfirm={handleFinalizeQueryMutation.mutate}
                    isOpen={showCreditUsageConfirmModal}
                    toggle={() => setShowCreditUsageConfirmModal(false)}
                />
            </Col>
        </Row>
    );
}

export default withNamespaces()(
    Confirmation
)